/**
 * Vue mixin for dealing with global keyboard input.
 * It automatically adds the listener and removes it upon component teardown.
 *
 * Just use the mixin and add the following data properties:
 * data() {
 *     return {
 *         keyboardListeners: {
 *             ArrowLeft: this.arrowLeftHandler,
 *         }
 *     }
 * }
 *
 * ...or use computed prop:
 * computed: {
 *     keyboardListeners() {
 *         return {
 *             ArrowLeft: this.arrowLeftHandler,
 *         }
 *     }
 * }
 *
 */
const keyboardListener = {
    data() {
        return {
            listener: null,
        };
    },
    mounted() {
        this.setKeyboardListener();
    },
    beforeDestroy() {
        this.removeKeyboardListener();
    },
    watch: {
        keyboardListeners: {
            immediate: true,
            handler: 'setKeyboardListener',
        },
    },
    methods: {
        setKeyboardListener() {
            this.removeKeyboardListener();
            this.listener = this.createKeyboardListener();
            this.addKeyboardListener();
        },
        addKeyboardListener() {
            document.addEventListener('keydown', this.listener);
        },
        removeKeyboardListener() {
            document.removeEventListener('keydown', this.listener);
        },
        createKeyboardListener() {
            const keysToListen = Object.keys(this.keyboardListeners);

            return (event) => {
                if (!keysToListen.includes(event.code)) {
                    return;
                }

                const handler = this.keyboardListeners[event.code];
                if (typeof handler === 'function') {
                    handler(event);
                }
            };
        },
        isCtrlPressed(event) {
            if (this.isMac) {
                return event.metaKey;
            }
            return event.ctrlKey;
        },
    },
    computed: {
        isMac() {
            return window.navigator?.platform?.startsWith('Mac');
        },
    },
};

export default keyboardListener;
